import { isPresent } from '@mbfab/shared'
import { z } from 'zod'

import type { ColumnConfig } from './main-row'
import { getXForColumns } from './main-row'

//
// v1 apps/frontend/client/src/components/MillsheetPrintForm/MillsheetListSvgV2/Header.tsx の移植。
// Figma からの自動生成 id（\xE5\xB7... のような16進エスケープの日本語ラベル）は座標・見た目に影響しない
// 装飾情報のため、移植にあたって削っている（意味のある id のみ残す）。
//
export const headerLabelTextConfigSchema = z.object({
  listTitle: z.string().nullish(),
  summaryTitle: z.string().nullish(),
  constructionManager: z.string().nullish(),
  constructionManagerName: z.string().nullish(),
  constructionExecutor: z.string().nullish(),
  constructionExecutorName: z.string().nullish(),
})
export type HeaderLabelTextConfig = z.infer<typeof headerLabelTextConfigSchema>

export const DEFAULT_LIST_TITLE = '鋼材ミルシート一覧表'
export const DEFAULT_SUMMARY_TITLE = '鋼材ミルシート集計表'
export const DEFAULT_CONSTRUCTION_MANAGER = '工事監理者'
export const DEFAULT_CONSTRUCTION_EXECUTOR = '工事施行者\n（品質管理責任者）'

export function Header({
  constructionName,
  sumKg,
  columnConfigs,
  headerLabelTextConfig,
  asSummary,
}: {
  constructionName: string
  sumKg: string
  columnConfigs: ColumnConfig[]
  headerLabelTextConfig: HeaderLabelTextConfig
  asSummary: boolean
}) {
  const xForColumns = getXForColumns(columnConfigs, 860, 95)
  const title = asSummary ? headerLabelTextConfig.summaryTitle : headerLabelTextConfig.listTitle
  const defaultTitle = asSummary ? DEFAULT_SUMMARY_TITLE : DEFAULT_LIST_TITLE

  return (
    <>
      {/* タイトル要素 */}
      <text
        fill="black"
        xmlSpace="preserve"
        style={{ whiteSpace: 'pre' }}
        fontFamily="Hiragino Kaku Gothic"
        fontSize={38}
        fontWeight={800}
        letterSpacing="0em"
        textAnchor="middle"
      >
        <tspan x={528} y={183.94}>
          {isPresent(title) ? title : defaultTitle}
        </tspan>
      </text>
      {/* 上部ヘッダー */}
      <g id="upper-header">
        <rect x={95} y={220} width={860} height={90} stroke="black" strokeWidth={2} />
        <rect x={95} y={310} width={860} height={90} stroke="black" strokeWidth={2} />
        <line x1={620} y1={220} x2={620} y2={400} stroke="black" strokeWidth={2} />
        <line x1={182} y1={221} x2={182} y2={270} stroke="black" strokeWidth={2} />
        <line x1={95} y1={269} x2={619} y2={269} stroke="black" strokeWidth={2} />
        <line x1={95} y1={355} x2={619} y2={355} stroke="black" strokeWidth={2} />
        <line x1={752} y1={221} x2={752} y2={401} stroke="black" strokeWidth={2} />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={18}
          letterSpacing="0em"
        >
          <tspan x={104} y={250.34}>
            工事名称
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={autoResizeFontSizeForConstructureName(constructionName)}
          letterSpacing="0em"
        >
          <tspan x={200} y={250.34}>
            {constructionName}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={15}
          letterSpacing="0em"
          textAnchor="middle"
        >
          {(() => {
            const centerX = 688 // 工事監理者の値の中央座標
            const centerY = 273 // 工事監理者の値の中央y座標
            const lineHeight = 24
            const value = isPresent(headerLabelTextConfig.constructionManager)
              ? headerLabelTextConfig.constructionManager
              : DEFAULT_CONSTRUCTION_MANAGER
            const lines = value ? value.split('\n') : []
            if (lines.length === 2) {
              return (
                <>
                  <tspan x={centerX} y={centerY - lineHeight / 2}>
                    {lines[0]}
                  </tspan>
                  <tspan x={centerX} y={centerY + lineHeight / 2}>
                    {lines[1]}
                  </tspan>
                </>
              )
            }
            if (lines.length === 1 && typeof lines[0] === 'string' && lines[0] !== '') {
              return (
                <tspan x={centerX} y={centerY}>
                  {lines[0]}
                </tspan>
              )
            }
            return null
          })()}
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={15}
          textAnchor="middle"
          letterSpacing="0em"
        >
          {(() => {
            const centerX = 688 // 工事施行者の値の中央座標
            const centerY = 363 // 工事施行者の値の中央y座標
            const lineHeight = 24
            const value = isPresent(headerLabelTextConfig.constructionExecutor)
              ? headerLabelTextConfig.constructionExecutor
              : DEFAULT_CONSTRUCTION_EXECUTOR
            const lines = value ? value.split('\n') : []
            if (lines.length === 2) {
              return (
                <>
                  <tspan x={centerX} y={centerY - lineHeight / 2}>
                    {lines[0]}
                  </tspan>
                  <tspan x={centerX} y={centerY + lineHeight / 2}>
                    {lines[1]}
                  </tspan>
                </>
              )
            }
            if (lines.length === 1 && typeof lines[0] === 'string' && lines[0] !== '') {
              return (
                <tspan x={centerX} y={centerY}>
                  {lines[0]}
                </tspan>
              )
            }
            return null
          })()}
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={18}
          letterSpacing="0.2em"
        >
          <tspan x={201.8} y={296.34}>
            建築工事施行計画報告書受付番号
          </tspan>
        </text>
        <g id="upper-header-受付番号">
          <text
            fill="black"
            xmlSpace="preserve"
            style={{ whiteSpace: 'pre' }}
            fontFamily="Hiragino Kaku Gothic"
            fontSize={15}
            letterSpacing="0.2em"
          >
            <tspan x={218} y={339.2}>
              {'令和    年　 月　　日　 第　　    　号'}
            </tspan>
          </text>
          <text
            fill="black"
            xmlSpace="preserve"
            style={{ whiteSpace: 'pre' }}
            fontFamily="Hiragino Kaku Gothic"
            fontSize={15}
            letterSpacing="0em"
          >
            <tspan x={164} y={327.7}>
              {'区\n'}
            </tspan>
            <tspan x={164} y={347.7}>
              {'市'}
            </tspan>
          </text>
        </g>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={18}
          letterSpacing="0em"
        >
          <tspan x={161} y={383.34}>
            この建築物に使用した鋼材は、下表の通りです
          </tspan>
        </text>
      </g>

      {/* lower header */}
      <g id="lower-header">
        <g id="upper-columns">
          <line x1={356} y1={399} x2={356} y2={444} stroke="black" strokeWidth={2} />
          <rect x={95} y={400} width={860} height={45} stroke="black" strokeWidth={2} />
        </g>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={18}
          letterSpacing="0em"
        >
          <tspan x={372} y={428.34}>
            実施 {isPresent(sumKg) ? sumKg + ` kg` : ``}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Hiragino Kaku Gothic"
          fontSize={18}
          letterSpacing="0em"
        >
          <tspan x={130.695} y={429.34}>
            鋼材使用量の合計(kg)
          </tspan>
        </text>

        {/* ヘッダー列 */}
        <g id="columns-header">
          <rect x={95} y={445} width={860} height={45} stroke="black" strokeWidth={2} />
          {xForColumns.map(({ columnRight }, index) => {
            return (
              <line key={index} x1={columnRight} y1={445} x2={columnRight} y2={490} stroke="black" strokeWidth={2} />
            )
          })}
        </g>
        {xForColumns.map(({ columnCenter, columnConfig }, index) => {
          return (
            <text
              key={index}
              fill="black"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontFamily="Hiragino Kaku Gothic"
              fontSize={HeaderCellFontSize({ columnConfig })}
              letterSpacing="0.4em"
              textAnchor="middle"
            >
              <HeaderCell columnConfig={columnConfig} columnCenter={columnCenter} />
            </text>
          )
        })}
      </g>
    </>
  )
}

function HeaderCell({ columnConfig, columnCenter }: { columnConfig: ColumnConfig; columnCenter: number }) {
  switch (columnConfig.type) {
    case 'steelType':
      return (
        <tspan x={columnCenter} y={474}>
          規格
        </tspan>
      )
    case 'sectionType':
      return (
        <tspan x={columnCenter} y={474}>
          形状
        </tspan>
      )
    case 'size':
      return (
        <tspan x={columnCenter} y={474}>
          寸法
        </tspan>
      )
    case 'thicknessDiameter':
      return (
        <tspan x={columnCenter} y={474}>
          厚・径
        </tspan>
      )
    case 'length':
      return (
        <tspan x={columnCenter} y={474}>
          長さ
        </tspan>
      )
    case 'quantity':
      return (
        <tspan x={columnCenter} y={474}>
          数量
        </tspan>
      )
    case 'weight':
      return (
        <>
          <tspan x={columnCenter} y={462}>
            重量
          </tspan>
          <tspan x={columnCenter} y={485.08}>
            {'(kg)'}
          </tspan>
        </>
      )
    case 'heatNumber':
      return (
        <tspan x={columnCenter} y={474}>
          製鋼番号
        </tspan>
      )
    case 'individualNumber':
      return (
        <tspan x={columnCenter} y={474}>
          個体番号
        </tspan>
      )
    case 'no':
      return (
        <tspan x={columnCenter} y={474}>
          証明書番号
        </tspan>
      )
    case 'manufacturerName':
      return (
        <tspan x={columnCenter} y={474}>
          メーカー名
        </tspan>
      )
    case 'constructionArea':
      return (
        <tspan x={columnCenter} y={474}>
          工区
        </tspan>
      )
    case 'member':
      return (
        <tspan x={columnCenter} y={474}>
          節
        </tspan>
      )
    case 'buildingPart':
      return (
        <tspan x={columnCenter} y={474}>
          部位
        </tspan>
      )
    case 'memo':
      return (
        <tspan x={columnCenter} y={474}>
          備考
        </tspan>
      )
    case 'pageNumber':
      return (
        <tspan x={columnCenter} y={474}>
          ページ番号
        </tspan>
      )
    case 'tradingCompany':
      // 発注照合の集計表の項目名（v1 要件定義書 07/06 版 3.2）。既存の汎用ミルシート印刷では
      // この列を使わないため、この表記変更が既存の帳票に出ることはない
      return (
        <tspan x={columnCenter} y={474}>
          商社名
        </tspan>
      )
    case 'orderNumber':
      return (
        <tspan x={columnCenter} y={474}>
          発注番号
        </tspan>
      )
    case 'innerNo':
      return (
        <tspan x={columnCenter} y={474}>
          内部番号
        </tspan>
      )
    case 'endorsementCompany':
      return (
        <tspan x={columnCenter} y={474}>
          裏書会社名
        </tspan>
      )
    // 裏書日は Excel（使用鋼材一覧表）専用の列で、この帳票の選択肢には出さない。
    // ColumnConfig['type'] の網羅性チェックのためだけにケースを用意する
    case 'endorsementDate':
      return (
        <tspan x={columnCenter} y={474}>
          裏書日
        </tspan>
      )
    case 'customer':
      return (
        <tspan x={columnCenter} y={474}>
          需要家名
        </tspan>
      )
    case 'referenceNumber':
      return (
        <tspan x={columnCenter} y={474}>
          参照番号
        </tspan>
      )
    // サプライヤー名は Excel（使用鋼材一覧表）専用の列で、この帳票（発注照合の集計表 SVG）の
    // 選択肢には出さない（print-columns.ts の REORDERABLE_COLUMN_TYPES / FIXED_ORDER_COLUMN_TYPES 参照）。
    // ColumnConfig['type'] の網羅性チェックのためだけにケースを用意する
    case 'supplierName':
      return (
        <tspan x={columnCenter} y={474}>
          サプライヤー名
        </tspan>
      )
    default: {
      const exhaustiveCheck: never = columnConfig.type
      return exhaustiveCheck
    }
  }
}

function HeaderCellFontSize({ columnConfig }: { columnConfig: ColumnConfig }) {
  switch (columnConfig.type) {
    case 'steelType':
      return 15
    case 'weight':
      return 15
    case 'no':
      return 13
    case 'heatNumber':
      return 15
    // 4〜5 文字の見出しは証明書番号と同じく縮小して収める。
    // supplierName（「サプライヤー名」7文字）はこの帳票の選択肢には出ないが、
    // 将来集計表の選択肢に加えたときに見出しが崩れないよう同じ縮小率にしておく
    case 'endorsementCompany':
    case 'customer':
    case 'supplierName':
      return 13
    case 'referenceNumber':
      return 15
    default:
      return 17
  }
}

function autoResizeFontSizeForConstructureName(text: string) {
  if (text.length > 39) {
    return 9
  }
  if (text.length > 36) {
    return 11
  }
  if (text.length > 33) {
    return 12
  }
  if (text.length > 30) {
    return 13
  }
  if (text.length > 26) {
    return 14
  }
  if (text.length > 23) {
    return 15
  }
  if (text.length > 20) {
    return 16
  }
  if (text.length > 17) {
    return 17
  }
  return 18
}
